import qs from 'query-string';
import React, { createContext, useCallback, useEffect, useState } from 'react';

import { ClientIds, IDW_FLOW_PARAM } from './constants';

export type Params = Record<string, string | string[] | null> & {
  // For all internal MGM and third-party clients
  client_id?: string;
  redirect_uri?: string;
  scopes?: string;
  // For third-party clients only (e.g., betmgm, passkey, holiday gift shoppe, etc.)
  state?: string;
  code_challenge?: string;
};

type Context = {
  reset: () => void;
  clear: () => void;
  force: (params: Params) => void;
  params: Params;
};

const nonCachedParameters = ['message'];

export const ignoreParams = ['partner', 'tokenKey', 'message', 'accessToken', IDW_FLOW_PARAM];

export function getPersistedParameters() {
  const rawSessionParams = window.sessionStorage.getItem('mgm_signIn_params') || '{}';

  //default paramerters to consider if they don't exist in session storage or query params
  const defaultParams = { client_id: ClientIds.MGM_APP_WEB } as Params;
  // get the saved parameters
  const sessionParams: Params = JSON.parse(rawSessionParams);
  // get the new query parameters
  const queryParams = qs.parse(window.location.search) as Params;
  // get the combined parameters
  const allParams: Params = { ...defaultParams, ...sessionParams, ...queryParams };

  return allParams;
}

export function clearPersistedParameters() {
  window.sessionStorage.removeItem('mgm_signIn_params');
}

export const PersistentParametersContext = createContext<Context>({} as Context);

export const usePersistentParameters = () => {
  const context: Context = React.useContext<Context>(PersistentParametersContext);

  if (context === undefined) {
    throw new Error('usePersistentParameters must be used within a PersistentParametersContext');
  }

  return context;
};

export const PersistentParametersContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const [params, setParams] = useState<Params>({} as Params);

  const set = useCallback(() => {
    const allParams = getPersistedParameters();
    const newParams = { ...allParams };

    nonCachedParameters.forEach(param => delete newParams[param]);

    // if they are different than what we currently have, persist them to session storage and state
    if (JSON.stringify(newParams) !== JSON.stringify(params)) {
      window.sessionStorage.setItem('mgm_signIn_params', JSON.stringify(newParams));
      setParams(newParams);
    }
  }, [params]);

  const clear = useCallback(() => {
    clearPersistedParameters();
  }, []);

  const reset = useCallback(() => {
    clear();
    set();
  }, [clear, set]);

  const force = useCallback((params: Params) => {
    window.sessionStorage.setItem('mgm_signIn_params', JSON.stringify(params));
    setParams(params);
  }, []);

  useEffect(() => {
    set();
  }, [set]);

  return (
    <PersistentParametersContext.Provider value={{ clear, reset, force, params }}>
      {children}
    </PersistentParametersContext.Provider>
  );
};
